import {
  isPayoutOperationBlocked,
  isSaleOperationBlocked,
} from '@shared/lib/auth/operation-eligibility'

export type AccountPresentation = {
  badge: string
  description: string
  tone: 'success' | 'awaiting' | 'closed'
}

export function accountPresentation(input: {
  isSaleBlocked?: boolean
  documentationStatus?: string
  operations?: {
    sales: 'waiting' | 'in_review' | 'released' | 'blocked'
    payouts: 'waiting' | 'in_review' | 'released' | 'blocked'
  }
}): AccountPresentation {
  const saleBlocked = input.operations
    ? input.operations.sales === 'blocked'
    : isSaleOperationBlocked(input)
  const payoutBlocked = isPayoutOperationBlocked({ payouts: input.operations?.payouts })
  if (saleBlocked && payoutBlocked) {
    return {
      badge: 'Conta com restrições',
      description:
        'Vendas e repasses têm restrições operacionais. Consulte seu cadastro para regularizar.',
      tone: 'closed',
    }
  }
  if (saleBlocked) {
    return {
      badge: 'Vendas bloqueadas',
      description:
        'Novas vendas estão bloqueadas. Consulte seu cadastro e acompanhe os repasses em Recebimentos.',
      tone: 'closed',
    }
  }
  if (payoutBlocked) {
    return {
      badge: 'Repasses com restrições',
      description:
        'Os repasses têm uma restrição operacional informada no cadastro. Revise as pendências para regularizar.',
      tone: 'awaiting',
    }
  }
  if (input.operations?.sales === 'released' && input.operations.payouts === 'released') {
    return {
      badge: 'Conta ativa',
      description:
        'Vendas e repasses estão habilitados no cadastro. Consulte Recebimentos para acompanhar as operações.',
      tone: 'success',
    }
  }
  return {
    badge: 'Aguardando confirmação',
    description:
      'Consulte a situação operacional no cadastro para confirmar a habilitação das vendas e dos repasses.',
    tone: 'awaiting',
  }
}

type OnboardingBadge = 'in_progress' | 'in_review' | 'correction_needed' | 'approved'

/** Call to action of the registration card, from the seller's onboarding badge. */
export function registrationActionLabel(badge: OnboardingBadge | undefined): string {
  if (badge === 'approved') return 'Ver cadastro'
  if (badge === 'in_review') return 'Acompanhar análise'
  return 'Continuar cadastro'
}

/** Bank card copy: the active payout account when there is one, otherwise the call to register. */
export function bankCardPresentation(
  activeAccount: { bankName?: string | null; last4?: string | null } | undefined,
): { text: string; action: string } {
  if (!activeAccount) {
    return { text: 'Cadastre a conta que vai receber os repasses.', action: 'Cadastrar conta' }
  }
  const bank = activeAccount.bankName ?? 'Conta'
  const last4 = activeAccount.last4 ? ` · •••• ${activeAccount.last4}` : ''
  return { text: `${bank}${last4} recebe seus repasses.`, action: 'Gerenciar conta' }
}
